// src/stores/crmClientStore.js
import { defineStore } from "pinia";
import { ref } from "vue";
import clientService from "@/services/crm/crmClient.service";
import * as Toast from "@/utils/toast";
import { createScopedCache } from "./_cache";
import { useAccessStore } from "@/stores/accessStore";
import { MODULE_KEYS } from "@/config/modules-registry";
import { isPlanUnavailableError } from "@/utils/planAccess";

// ✅ Helper to handle different toast API formats
const notifyError = (msg) => {
  if (Toast?.toast?.error) return Toast.toast.error(msg);
  if (typeof Toast?.toast === "function") return Toast.toast(msg, "error");
  if (Toast?.error) return Toast.error(msg);
  if (typeof Toast === "function") return Toast(msg, "error");
  console.error(msg);
};

const notifySuccess = (msg) => {
  if (Toast?.toast?.success) return Toast.toast.success(msg);
  if (typeof Toast?.toast === "function") return Toast.toast(msg, "success");
  if (Toast?.success) return Toast.success(msg);
  if (typeof Toast === "function") return Toast(msg, "success");
  console.log(msg);
};

const clientListCache = createScopedCache("crm:clients:list");
const clientDetailCache = createScopedCache("crm:clients:detail");

const CLIENTS_LIST_TTL = 2 * 60 * 1000;
const CLIENTS_DETAIL_TTL = 5 * 60 * 1000;

export const useCrmClientStore = defineStore("crmClient", () => {
  const clients = ref([]);
  const client = ref(null);
  const loading = ref(false);
  const error = ref(null);

  // ✅ Added 'list' alias to match smartFetch expectations
  async function list(params = {}) {
    return await fetchClients(params);
  }

  async function fetchClients(params = {}) {
    const silent = params.silent === true;
    const access = useAccessStore();

    if (!access.loaded) {
      await access.ensureAccess();
    }

    if (!access.hasModule(MODULE_KEYS.CRM_CLIENTS)) {
      clients.value = [];
      return {
        data: {
          data: [],
          meta: {},
        },
      };
    }

    loading.value = true;
    error.value = null;
    try {
      const payload = await clientListCache.get(params, {
        ttlMs: CLIENTS_LIST_TTL,
        force: params.force === true,
        loader: async () => {
          const res = await clientService.getAll(params);
          const data = res.data.data || res.data;
          const rows = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
          return {
            clients: rows,
            meta: res.data.meta || data?.meta || {},
          };
        },
        onHydrate: ({ clients: rows }) => {
          clients.value = rows || [];
        },
      });
      
      // ✅ Return in the format expected by smartFetch
      return {
        data: {
          data: clients.value,
          meta: payload.meta || {}
        }
      };
    } catch (e) {
      if (silent && isPlanUnavailableError(e)) {
        clients.value = [];
        return {
          data: {
            data: [],
            meta: {},
          },
        };
      }

      error.value = e?.response?.data?.message || "Failed to fetch clients";
      notifyError(error.value);
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function ensureClients(params = {}) {
    return await fetchClients({ ...params, force: false, silent: true });
  }

  async function fetchClient(id) {
    loading.value = true;
    error.value = null;
    try {
      const payload = await clientDetailCache.get(
        { id: Number(id) || id },
        {
          ttlMs: CLIENTS_DETAIL_TTL,
          loader: async () => {
            const res = await clientService.get(id);
            return { client: res.data?.data || res.data };
          },
          onHydrate: ({ client: row }) => {
            client.value = row || null;
          },
        }
      );
      client.value = payload.client || null;
      return client.value;
    } catch (e) {
      error.value = e?.response?.data?.message || "Failed to fetch client";
      notifyError(error.value);
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function ensureClient(id) {
    return await fetchClient(id);
  }

  async function createClient(payload) {
    loading.value = true;
    error.value = null;
    try {
      const res = await clientService.create(payload);
      invalidateClientCaches();
      notifySuccess("Client created successfully");
      await fetchClients({ force: true });
      return res.data;
    } catch (e) {
      error.value = e?.response?.data?.message || "Failed to create client";
      notifyError(error.value);
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function updateClient(id, payload) {
    loading.value = true;
    error.value = null;
    try {
      const res = await clientService.update(id, payload);
      invalidateClientCaches(id);
      notifySuccess("Client updated successfully");
      await fetchClients({ force: true });
      return res.data;
    } catch (e) {
      error.value = e?.response?.data?.message || "Failed to update client";
      notifyError(error.value);
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function deleteClient(id) {
    loading.value = true;
    error.value = null;
    try {
      await clientService.delete(id);
      invalidateClientCaches(id);
      notifySuccess("Client deleted successfully");
      await fetchClients({ force: true });
    } catch (e) {
      error.value = e?.response?.data?.message || "Failed to delete client";
      notifyError(error.value);
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function bulkDelete(ids) {
    loading.value = true;
    error.value = null;
    try {
      await clientService.bulkDelete(ids);
      invalidateClientCaches();
      notifySuccess("Clients deleted successfully");
      await fetchClients({ force: true });
    } catch (e) {
      error.value = e?.response?.data?.message || "Failed to bulk delete clients";
      notifyError(error.value);
      throw e;
    } finally {
      loading.value = false;
    }
  }

  function invalidateClientCaches(id = null) {
    clientListCache.clear();
    if (id != null) {
      clientDetailCache.invalidate({ id: Number(id) || id });
    } else {
      clientDetailCache.clear();
    }
  }

  return {
    clients,
    client,
    loading,
    error,
    list, // ✅ Export the list method
    fetchClients,
    ensureClients,
    fetchClient,
    ensureClient,
    createClient,
    updateClient,
    deleteClient,
    bulkDelete,
    invalidateClientCaches,
  };
});
